// src/utils/pdfGenerator.js

import { PDFDocument, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';

let cachedCalibriBold = null;

const loadCalibriBold = async () => {
    if (cachedCalibriBold) return cachedCalibriBold;
    try {
        const res = await fetch('/fonts/Calibri-Bold.ttf');
        if (!res.ok) throw new Error('Fonte Calibri-Bold.ttf não encontrada');
        cachedCalibriBold = await res.arrayBuffer();
        return cachedCalibriBold;
    } catch (error) {
        console.error("Erro ao carregar fonte:", error);
        return null;
    }
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const tzOffset = date.getTimezoneOffset() * 60000;
    const corrected = new Date(date.getTime() + tzOffset);
    return corrected.toLocaleDateString('pt-BR');
};

// Função para aplicar a máscara no CPF (se for CNS, ele deixa original)
const formatCPF = (documento) => {
    if (!documento) return '';
    const limpo = String(documento).replace(/\D/g, ''); // Remove tudo que não for número
    if (limpo.length === 11) {
        return limpo.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return documento; // Retorna normal se for CNS (15 dígitos) ou outro formato
};

const COORDS = {
    equipe: { x: 220, y: 512.254 },
    microarea: { x: 295, y: 512.254 },
    acs: { x: 345, y: 512.254 },
    primeiraLinhaY: 484.3,
    alturaLinha: 16.8,
    colunas: {
        nome: { x: 112, maxWidth: 126 },
        nascimento: { x: 243, maxWidth: 38 },
        idade: { x: 283, maxWidth: 14 },
        endereco: { x: 300, maxWidth: 155 },
        acao: { x: 458, maxWidth: 175 },
        cpf: { x: 655, maxWidth: 75 }
    }
};

const truncateText = (text, font, fontSize, maxWidth) => {
    if (!text) return '';
    let result = text;
    let width = font.widthOfTextAtSize(result, fontSize);
    while (width > maxWidth && result.length > 0) {
        result = result.slice(0, -1);
        width = font.widthOfTextAtSize(result + '...', fontSize);
    }
    return result === text ? text : result + '...';
};

export const generateVisitPDF = async (
    visitList,
    acsInfo,
    outputType = 'save',
    templateBytes = null,
    addBlankPage = false 
) => {
    const pdfDoc = await PDFDocument.create();
    pdfDoc.registerFontkit(fontkit);

    const calibriBoldBytes = await loadCalibriBold();
    if (!calibriBoldBytes) return alert("Erro ao carregar fonte!");
    const calibriBold = await pdfDoc.embedFont(calibriBoldBytes);

    let sourceDoc;
    if (templateBytes) {
        sourceDoc = await PDFDocument.load(templateBytes);
    } else {
        sourceDoc = await PDFDocument.create();
        sourceDoc.addPage([842, 595]);
    }

    const MAX_ROWS = 26;
    let currentPage = null;
    let currentY = COORDS.primeiraLinhaY;
    let rowCount = 0;

    const addNewPage = async () => {
        const [copiedPage] = await pdfDoc.copyPages(sourceDoc, [0]);
        pdfDoc.addPage(copiedPage);
        
        const nomeFormatado = acsInfo.nome ? acsInfo.nome.split(' ').slice(0, 2).join(' ') : '';
        const matchMicro = acsInfo.microarea ? acsInfo.microarea.match(/MICROAREA\s+(\d+)/i) : null;
        const microareaFormatada = matchMicro ? matchMicro[1] : (acsInfo.microarea || '');
        const matchEquipe = acsInfo.microarea ? acsInfo.microarea.match(/EQ\s+(\d+)/i) : null;
        const equipeFormatada = acsInfo.equipe || (matchEquipe ? matchEquipe[1] : '');

        const headerFontSize = 8;
        copiedPage.drawText(equipeFormatada, { x: COORDS.equipe.x, y: COORDS.equipe.y, size: headerFontSize, font: calibriBold, color: rgb(0, 0, 0) });
        copiedPage.drawText(microareaFormatada, { x: COORDS.microarea.x, y: COORDS.microarea.y, size: headerFontSize, font: calibriBold, color: rgb(0, 0, 0) });
        copiedPage.drawText(nomeFormatado, { x: COORDS.acs.x, y: COORDS.acs.y, size: headerFontSize, font: calibriBold, color: rgb(0, 0, 0) });

        currentY = COORDS.primeiraLinhaY;
        rowCount = 0;
        return copiedPage;
    };

    currentPage = await addNewPage();
    const tableFontSize = 6; 

    for (const house of visitList) {
        const members = house.members.filter(m => m.isSelected);
        if (members.length === 0) continue;

        const rowsNeeded = members.length + 1;
        if (rowCount + rowsNeeded > MAX_ROWS) {
            currentPage = await addNewPage();
        }

        for (const member of members) {
            currentPage.drawText(truncateText(member.nome, calibriBold, tableFontSize, COORDS.colunas.nome.maxWidth), { x: COORDS.colunas.nome.x, y: currentY, size: tableFontSize, font: calibriBold });
            currentPage.drawText(truncateText(formatDate(member.dt_nascimento), calibriBold, tableFontSize, COORDS.colunas.nascimento.maxWidth), { x: COORDS.colunas.nascimento.x, y: currentY, size: tableFontSize, font: calibriBold });
            currentPage.drawText(member.idade ? String(member.idade) : '', { x: COORDS.colunas.idade.x, y: currentY, size: tableFontSize, font: calibriBold });
            
            // 🔥 CORREÇÃO: Utilizando a exibição de endereço da casa (house) que já vem limpa e formatada do Front-end!
            currentPage.drawText(truncateText(house.addressDisplay || '', calibriBold, tableFontSize, COORDS.colunas.endereco.maxWidth), { x: COORDS.colunas.endereco.x, y: currentY, size: tableFontSize, font: calibriBold });
            
            currentPage.drawText(truncateText(member.reason || '', calibriBold, tableFontSize, COORDS.colunas.acao.maxWidth), { x: COORDS.colunas.acao.x, y: currentY, size: tableFontSize, font: calibriBold });
            
            // 🔥 CORREÇÃO: Ordem de prioridade invertida. Busca o CPF primeiro. Se não achar, coloca o CNS.
            const docFormatado = formatCPF(member.cpf || member.cns || '');
            currentPage.drawText(truncateText(docFormatado, calibriBold, tableFontSize, COORDS.colunas.cpf.maxWidth), { x: COORDS.colunas.cpf.x, y: currentY, size: tableFontSize, font: calibriBold });
            
            currentY -= COORDS.alturaLinha;
            rowCount++;
        }

        if (rowCount < MAX_ROWS) {
            currentY -= COORDS.alturaLinha;
            rowCount++;
        }
    }

    if (addBlankPage) {
        await addNewPage(); 
    }

    const pdfBytes = await pdfDoc.save();

    if (outputType === 'blob') {
        return new Blob([pdfBytes], { type: 'application/pdf' });
    }

    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `folha-visita-${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
};